import { Button, Dropdown, Modal, Tabs } from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import {
  useIndicatorPermission,
  useProjectPermission,
} from '@/features/authentication/_hooks/permission';
import { useProjectDetailSearchQueryStates } from '@/features/project/_hooks/search-query-states';
import { activityTabKeys } from '@/features/project/_utils/search-query-states';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { generateUuid } from '@/utils/generate-uuid';
import { DownOutlined } from '@ant-design/icons';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { ActivityDetails } from './ActivityDetails';
import { ActivityFlow } from './activity-flow/ActivityFlow';

type Props = {
  editable: z.infer<typeof ProjectModel>['isLocked'];
};

export const ActivitiesInformation: FC<Props> = ({ editable }) => {
  const { t } = useTranslation();
  const { setDrawerState } = useProjectDetailDrawerState();
  const editProjectPermission = useProjectPermission(true);
  const editIndicatorPermission = useIndicatorPermission(true);
  const canEdit =
    editProjectPermission.data && editIndicatorPermission.data && editable;

  const [treeModifyCount, setTreeModifyCount] = useState(0);
  const INITIAL_COUNT = 3;

  const [openDirtyDialog, setOpenDirtyDialog] = useState(false);
  // to rollback activity flow
  const [renderKey, setRenderKey] = useState('');
  const [{ activitiesTabKey }, setSearchQueryStates] =
    useProjectDetailSearchQueryStates();
  return (
    <>
      <Tabs
        type="card"
        activeKey={activitiesTabKey}
        destroyInactiveTabPane
        onChange={(activeKey) => {
          setSearchQueryStates((v) => ({
            ...v,
            activitiesTabKey: activeKey as z.infer<typeof activityTabKeys>,
          }));
        }}
        items={[
          {
            key: activityTabKeys.enum.activities,
            label: t('activities'),
            children: <ActivityDetails />,
          },
          {
            key: activityTabKeys.enum['activity-flow'],
            label: t('connection-of-activities-and-performance-target'),
            children: (
              <ActivityFlow
                key={renderKey}
                setTreeModifyCount={setTreeModifyCount}
              />
            ),
          },
        ]}
        {...(canEdit && {
          tabBarExtraContent:
            activitiesTabKey === 'activities' ? (
              <PublishedProjectMutableGuard>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'activity',
                        label: t('activity'),
                      },
                      {
                        key: 'activity-indicator-form',
                        label: t('indicator'),
                      },
                    ],
                    onClick: (e) => {
                      switch (e.key) {
                        case 'activity':
                        case 'activity-indicator-form':
                          setDrawerState({
                            open: true,
                            type: e.key,
                            isEditing: false,
                          });
                          break;
                        default:
                      }
                    },
                  }}
                >
                  <Button type="primary">
                    {t('create')}
                    <DownOutlined aria-label="" />
                  </Button>
                </Dropdown>
              </PublishedProjectMutableGuard>
            ) : (
              <PublishedProjectMutableGuard>
                <Button
                  type="primary"
                  onClick={() => {
                    // 1st count is back box attach
                    // 2nd count is tree fetched modification
                    // 3rd count is back box deformation when tree fetched
                    if (treeModifyCount > 3) {
                      setOpenDirtyDialog(true);
                    } else {
                      setDrawerState({
                        open: true,
                        type: 'activity-tree-node',
                      });
                    }
                  }}
                >
                  {t('edit-connection')}
                </Button>
              </PublishedProjectMutableGuard>
            ),
        })}
      />
      <Modal
        title={t('activity-tree-is-dirty-title')}
        open={openDirtyDialog}
        onOk={() => {
          setOpenDirtyDialog(false);
          setRenderKey(generateUuid());
          setDrawerState({ open: true, type: 'activity-tree-node' });
          setTreeModifyCount(INITIAL_COUNT);
        }}
        onCancel={() => {
          setOpenDirtyDialog(false);
        }}
      >
        {t('activity-tree-is-dirty-description')}
      </Modal>
    </>
  );
};
